/* eslint-disable @typescript-eslint/no-empty-function */
import { Injectable } from '@angular/core';
import { AuthenticationService } from '@app/core/authentication/authentication.service';
import { ENDPOINTS } from '@app/core/constants/endpoints';
import { WindowRefService } from '@app/core/services/helpers/window-ref.service';
import { UserService } from '@app/core/services/users/user.service';
import { SelectedAccountStore } from '@app/core/stores/selected-account-store.service';
import { APP_MENU } from '@app/shared/constants/menu';
import { CompanyAccount } from '@app/shared/models/company-account.model';
import { AppMenu, NavItem, AppTopNavigation } from '@app/shared/models/nav-item.model';
import { AuthenticatedUser } from '@app/shared/models/authenticated-user.model';
import { Router } from '@angular/router';

@Injectable()
export class AppMenuBuilderService {
    private window: Window;
    private user: AuthenticatedUser;
    private selectedAccount: CompanyAccount;
    private accountsMenu: NavItem[];
    private topNavigationMenu: AppTopNavigation;
    private sideNavigationMenu: NavItem[];
    menuItems: any;

    constructor(
        private usersService: UserService,
        private authenticationService: AuthenticationService,
        private selectedAccountStore: SelectedAccountStore,
        private router: Router,
        private windowRefService: WindowRefService
    ) {
        this.window = this.windowRefService.window;

        this.menuItems = {
            COMPANY_SETTINGS: {
                displayName: APP_MENU.ADMIN,
                iconName: 'settings',
                command: () => {
                    this.router.navigate([ENDPOINTS.urls.companySettings]).catch(() => {});
                },
            },
            ACCOUNTS: {
                displayName: APP_MENU.ACCOUNTS,
                command: () => {},
            },
            PROFILE: {
                displayName: APP_MENU.PROFILE_SETTINGS,
                iconName: 'manage_accounts',
                command: () => {
                    this.router.navigate([ENDPOINTS.urls.profileSettings]).catch(() => {});
                },
            },
            MODEL_VIEWER: {
                displayName: APP_MENU.MODEL_VIEWER,
                iconName: 'book',
                command: () => {
                    this.window.location.href = ENDPOINTS.urls.modelViewer;
                },
            },
            MODEL_MANAGER: {
                displayName: APP_MENU.MODEL_MANAGER,
                iconName: 'book',
                command: () => {
                    this.window.location.href = ENDPOINTS.urls.modelManager;
                },
            },
            LOGOUT: {
                displayName: APP_MENU.LOGOUT,
                iconName: 'logout',
                command: () => {
                    this.authenticationService.logout(true).catch(() => {});
                },
            },
        };
    }

    createAppMenu(user: AuthenticatedUser, account: CompanyAccount): AppMenu {
        this.user = user;
        this.selectedAccount = account;
        this.accountsMenu = this.getCompanyAccountsMenu();

        this.topNavigationMenu = this.createTopNavigationMenu();
        this.sideNavigationMenu = this.createSideNavigationMenu();

        return {
            top: this.topNavigationMenu,
            side: this.sideNavigationMenu,
            accountsMenu: this.accountsMenu,
        };
    }

    private createTopNavigationMenu(): AppTopNavigation {
        const modelItem = this.getModelMenuItem();

        return {
            inline: [this.menuItems.COMPANY_SETTINGS],
            userMenu: [
                ...(this.accountsMenu.length > 1
                    ? [
                          {
                              ...this.menuItems.ACCOUNTS,
                              children: this.accountsMenu,
                          },
                      ]
                    : []),
                this.menuItems.PROFILE,
                ...(modelItem ? [modelItem] : []),
                this.menuItems.LOGOUT,
            ],
        };
    }

    private createSideNavigationMenu(): NavItem[] {
        const modelItem = this.getModelMenuItem();

        return [
            ...this.topNavigationMenu.inline,
            ...(modelItem ? [modelItem] : []),
            this.menuItems.PROFILE as NavItem,
            this.menuItems.LOGOUT as NavItem,
        ];
    }

    private getCompanyAccountsMenu(): NavItem[] {
        const childAccounts = [...this.user.childAccounts].sort((a, b) =>
            a.name.localeCompare(b.name)
        );

        if (this.user.serviceAccount) {
            childAccounts.unshift(this.user.serviceAccount);
        }

        const accounts: NavItem[] = childAccounts.map((account) => ({
            displayName: account.name,
            active: this.selectedAccount.id === account.id,
            command: () => {
                this.selectedAccountStore.set(account);
            },
        }));

        return accounts;
    }

    private getModelMenuItem(): NavItem {
        let modelItem: NavItem = null;

        if (this.selectedAccount.isManagementAccount()) {
            modelItem = this.selectedAccount.isAdmin()
                ? this.menuItems.MODEL_MANAGER
                : this.menuItems.MODEL_VIEWER;
        }

        return modelItem;
    }
}
